import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Plus, Clock, Users, MapPin, Trash2, Loader2, Edit, ChevronRight, Check, X } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_EVENT_TYPES = [
  { value: 'reunion', label: 'Réunion', color: 'bg-blue-100 text-blue-700' },
  { value: 'formation', label: 'Formation', color: 'bg-purple-100 text-purple-700' },
  { value: 'projet', label: 'Projet', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'tache', label: 'Tâche spécifique', color: 'bg-amber-100 text-amber-700' },
  { value: 'autre', label: 'Autre', color: 'bg-slate-100 text-slate-700' }
];

const TYPE_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
  'bg-orange-100 text-orange-700',
  'bg-slate-100 text-slate-700'
];

const CALENDAR_EVENT_TYPES = {
  meeting: { label: 'Réunion', color: 'bg-blue-100 text-blue-700' },
  task: { label: 'Tâche', color: 'bg-amber-100 text-amber-700' },
  reminder: { label: 'Rappel', color: 'bg-purple-100 text-purple-700' },
  holiday: { label: 'Congé', color: 'bg-emerald-100 text-emerald-700' }
};

export default function PlanningPage() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('calendar');
  const [events, setEvents] = useState([]);
  const [timeEvents, setTimeEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  
  // Event types (modifiable list)
  const [eventTypes, setEventTypes] = useState(() => {
    const saved = localStorage.getItem('customEventTypes');
    return saved ? JSON.parse(saved) : DEFAULT_EVENT_TYPES;
  });
  const [newTypeName, setNewTypeName] = useState('');
  const [selectedColor, setSelectedColor] = useState(TYPE_COLORS[0]);
  const [showAddType, setShowAddType] = useState(false);
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [editShiftDialogOpen, setEditShiftDialogOpen] = useState(false);
  const [shiftDetailOpen, setShiftDetailOpen] = useState(false);
  
  const [submitting, setSubmitting] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  
  // Forms
  const [eventForm, setEventForm] = useState({ 
    title: '', 
    description: '', 
    start_date: '', 
    end_date: '', 
    event_type: 'meeting', 
    location: '' 
  });
  
  const [shiftForm, setShiftForm] = useState({
    name: '',
    description: '',
    event_type: 'autre',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    assigned_to: [],
    assign_all: false,
    tasks: [],
    notes: ''
  });
  
  const [newTask, setNewTask] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, timeEventsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/events`),
        axios.get(`${API_URL}/api/time-events`),
        isAdmin ? axios.get(`${API_URL}/api/users`) : Promise.resolve({ data: [] })
      ]);
      setEvents(eventsRes.data);
      setTimeEvents(timeEventsRes.data);
      setUsers(usersRes.data.filter(u => u.status === 'active'));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.start_date) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/events`, {
        ...eventForm,
        end_date: eventForm.end_date || eventForm.start_date
      });
      toast.success('Événement créé');
      setDialogOpen(false);
      setEventForm({ title: '', description: '', start_date: '', end_date: '', event_type: 'meeting', location: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    if (!shiftForm.name || !shiftForm.start_date) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    if (!shiftForm.assign_all && shiftForm.assigned_to.length === 0) {
      toast.error('Veuillez assigner au moins un utilisateur ou sélectionner "Tous les utilisateurs"');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/time-events`, shiftForm);
      toast.success('Shift créé et ajouté à la feuille des événements');
      setShiftDialogOpen(false);
      resetShiftForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateShift = async (e) => {
    e.preventDefault();
    if (!selectedShift) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/time-events/${selectedShift.id}`, {
        name: shiftForm.name,
        description: shiftForm.description,
        event_type: shiftForm.event_type,
        start_date: shiftForm.start_date,
        end_date: shiftForm.end_date,
        assigned_to: shiftForm.assigned_to,
        assign_all: shiftForm.assign_all,
        tasks: shiftForm.tasks,
        notes: shiftForm.notes
      });
      toast.success('Shift modifié');
      setEditShiftDialogOpen(false);
      setSelectedShift(null);
      resetShiftForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm('Supprimer ce shift ? Il sera également supprimé de la feuille des événements dans Suivi du temps.')) return;
    try {
      await axios.delete(`${API_URL}/api/time-events/${shiftId}`);
      toast.success('Shift supprimé');
      setShiftDetailOpen(false);
      setSelectedShift(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await axios.delete(`${API_URL}/api/events/${id}`);
      toast.success('Événement supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openEditShift = (shift) => {
    setSelectedShift(shift);
    setShiftForm({
      name: shift.name,
      description: shift.description || '',
      event_type: shift.event_type || 'autre',
      start_date: shift.start_date,
      end_date: shift.end_date || '',
      assigned_to: shift.assigned_to || [],
      assign_all: shift.assign_all || false,
      tasks: shift.tasks || [],
      notes: shift.notes || ''
    });
    setEditShiftDialogOpen(true);
  };

  const openShiftDetail = (shift) => {
    setSelectedShift(shift);
    setShiftDetailOpen(true);
  };

  const resetShiftForm = () => {
    setShiftForm({
      name: '',
      description: '',
      event_type: 'autre',
      start_date: format(selectedDate, 'yyyy-MM-dd'),
      end_date: '',
      assigned_to: [],
      assign_all: false,
      tasks: [],
      notes: ''
    });
    setNewTask('');
  };

  const addTaskToForm = () => {
    if (newTask.trim()) {
      setShiftForm(prev => ({ ...prev, tasks: [...prev.tasks, newTask.trim()] }));
      setNewTask('');
    }
  };

  const removeTaskFromForm = (index) => {
    setShiftForm(prev => ({ ...prev, tasks: prev.tasks.filter((_, i) => i !== index) }));
  };

  const toggleUserSelection = (userId) => {
    setShiftForm(prev => {
      if (prev.assigned_to.includes(userId)) {
        return { ...prev, assigned_to: prev.assigned_to.filter(id => id !== userId) };
      } else {
        return { ...prev, assigned_to: [...prev.assigned_to, userId] };
      }
    });
  };

  const addEventType = () => {
    if (newTypeName.trim()) {
      const value = newTypeName.trim().toLowerCase().replace(/\s+/g, '_');
      // Check if type already exists
      if (eventTypes.some(t => t.value === value)) {
        toast.error('Ce type existe déjà');
        return;
      }
      const newType = {
        value,
        label: newTypeName.trim(),
        color: selectedColor
      };
      const updatedTypes = [...eventTypes, newType];
      setEventTypes(updatedTypes);
      localStorage.setItem('customEventTypes', JSON.stringify(updatedTypes));
      setShiftForm(prev => ({ ...prev, event_type: value }));
      setNewTypeName('');
      setSelectedColor(TYPE_COLORS[0]);
      setShowAddType(false);
      toast.success('Type ajouté');
    }
  };

  const removeEventType = (typeValue) => {
    // Don't remove default types
    if (DEFAULT_EVENT_TYPES.some(t => t.value === typeValue)) {
      toast.error('Impossible de supprimer un type par défaut');
      return;
    }
    const updatedTypes = eventTypes.filter(t => t.value !== typeValue);
    setEventTypes(updatedTypes);
    localStorage.setItem('customEventTypes', JSON.stringify(updatedTypes));
    toast.success('Type supprimé');
  };

  const getEventTypeStyle = (type) => {
    return eventTypes.find(t => t.value === type)?.color || 'bg-slate-100 text-slate-700';
  };

  const getEventTypeLabel = (type) => {
    return eventTypes.find(t => t.value === type)?.label || type || 'Autre';
  };

  const filteredEvents = events.filter(e => e.start_date?.startsWith(format(selectedDate, 'yyyy-MM-dd')));
  const filteredShifts = timeEvents.filter(s => {
    const startDate = s.start_date;
    const endDate = s.end_date || s.start_date;
    const selected = format(selectedDate, 'yyyy-MM-dd');
    return startDate <= selected && endDate >= selected;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  // Reusable form JSX for shift creation/editing
  const renderShiftForm = (onSubmit, isEdit) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Nom de l'événement *</Label>
        <Input 
          value={shiftForm.name} 
          onChange={(e) => setShiftForm(prev => ({ ...prev, name: e.target.value }))} 
          placeholder="Ex: Réunion d'équipe hebdomadaire"
          data-testid="shift-name-input"
        />
      </div>
      
      {/* Type d'événement - Modifiable */}
      <div className="space-y-2">
        <Label>Type d'événement</Label>
        {!showAddType ? (
          <div className="flex gap-2">
            <Select value={shiftForm.event_type} onValueChange={(v) => setShiftForm(prev => ({ ...prev, event_type: v }))}>
              <SelectTrigger data-testid="shift-type-select" className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${type.color.split(' ')[0]}`}></span>
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" size="icon" onClick={() => setShowAddType(true)} title="Ajouter un type">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input 
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Nouveau type..."
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEventType();
                  }
                }}
              />
              <Button type="button" variant="outline" size="icon" onClick={addEventType} className="text-emerald-600">
                <Check className="w-4 h-4" />
              </Button>
              <Button type="button" variant="outline" size="icon" onClick={() => { setShowAddType(false); setNewTypeName(''); setSelectedColor(TYPE_COLORS[0]); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1">
              <span className="text-xs text-slate-500 mr-2">Couleur:</span>
              {TYPE_COLORS.map((color, i) => (
                <button
                  key={i}
                  type="button"
                  className={`w-6 h-6 rounded-full ${color.split(' ')[0]} border-2 ${selectedColor === color ? 'border-slate-900 scale-110' : 'border-transparent'} transition-transform`}
                  onClick={() => setSelectedColor(color)}
                  title={`Couleur ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}
        {eventTypes.length > DEFAULT_EVENT_TYPES.length && (
          <div className="flex flex-wrap gap-1 mt-2">
            {eventTypes.filter(t => !DEFAULT_EVENT_TYPES.some(d => d.value === t.value)).map(type => (
              <Badge key={type.value} variant="outline" className={`text-xs cursor-pointer hover:bg-red-50 ${type.color}`} onClick={() => removeEventType(type.value)}>
                {type.label} <X className="w-3 h-3 ml-1" />
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Dates sur la même ligne */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date de début *</Label>
          <Input 
            type="date" 
            value={shiftForm.start_date} 
            onChange={(e) => setShiftForm(prev => ({ ...prev, start_date: e.target.value }))}
            data-testid="shift-start-date"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Date de fin</Label>
          <Input 
            type="date" 
            value={shiftForm.end_date} 
            onChange={(e) => setShiftForm(prev => ({ ...prev, end_date: e.target.value }))}
            data-testid="shift-end-date"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea 
          value={shiftForm.description} 
          onChange={(e) => setShiftForm(prev => ({ ...prev, description: e.target.value }))} 
          rows={2}
          placeholder="Description de l'événement..."
        />
      </div>
      
      {/* User Assignment */}
      <div className="space-y-3">
        <Label>Assigner à</Label>
        <div className="flex items-center gap-2 mb-2">
          <Checkbox 
            id={isEdit ? "edit-shift-assign-all" : "shift-assign-all"}
            checked={shiftForm.assign_all}
            onCheckedChange={(checked) => setShiftForm(prev => ({ ...prev, assign_all: checked, assigned_to: [] }))}
          />
          <label htmlFor={isEdit ? "edit-shift-assign-all" : "shift-assign-all"} className="text-sm font-medium cursor-pointer">Tous les utilisateurs</label>
        </div>
        
        {!shiftForm.assign_all && (
          <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
            {users.map(u => (
              <div key={u.id} className="flex items-center gap-2">
                <Checkbox 
                  id={`${isEdit ? 'edit-' : ''}shift-user-${u.id}`}
                  checked={shiftForm.assigned_to.includes(u.id)}
                  onCheckedChange={() => toggleUserSelection(u.id)}
                />
                <label htmlFor={`${isEdit ? 'edit-' : ''}shift-user-${u.id}`} className="text-sm cursor-pointer">
                  {u.first_name} {u.last_name}
                  <span className="text-slate-400 ml-2">({u.department || 'N/A'})</span>
                </label>
              </div>
            ))}
          </div>
        )}
        {shiftForm.assigned_to.length > 0 && !shiftForm.assign_all && (
          <p className="text-xs text-slate-500">{shiftForm.assigned_to.length} utilisateur(s) sélectionné(s)</p>
        )}
      </div>
      
      {/* Tasks */}
      <div className="space-y-2">
        <Label>Tâches</Label>
        <div className="flex gap-2">
          <Input 
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="Ajouter une tâche..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTaskToForm();
              }
            }}
          />
          <Button type="button" variant="outline" onClick={addTaskToForm}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {shiftForm.tasks.length > 0 && (
          <div className="space-y-1 mt-2">
            {shiftForm.tasks.map((task, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded">
                <span className="text-sm">{task}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeTaskFromForm(i)}>
                  <Trash2 className="w-3 h-3 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea 
          value={shiftForm.notes} 
          onChange={(e) => setShiftForm(prev => ({ ...prev, notes: e.target.value }))} 
          rows={2}
          placeholder="Notes additionnelles..."
        />
      </div>
      
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => {
          isEdit ? setEditShiftDialogOpen(false) : setShiftDialogOpen(false);
          resetShiftForm();
        }}>Annuler</Button>
        <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="shift-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? 'Modifier' : 'Créer')}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="planning-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Planification</h1>
          <p className="text-slate-500 mt-1">Calendrier et planning des équipes</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-event-btn">
            <Plus className="w-4 h-4 mr-2" />Événement
          </Button>
          {isAdmin && (
            <Button onClick={() => {
              resetShiftForm();
              setShiftForm(prev => ({ ...prev, start_date: format(selectedDate, 'yyyy-MM-dd') }));
              setShiftDialogOpen(true);
            }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-shift-btn">
              <Clock className="w-4 h-4 mr-2" />Shift
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardContent className="p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={fr}
              className="rounded-md"
            />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-blue-600" />
              {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="calendar">Événements</TabsTrigger>
                <TabsTrigger value="shifts" data-testid="shifts-tab">
                  Planning équipe
                  {filteredShifts.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{filteredShifts.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="calendar">
                {filteredEvents.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun événement ce jour</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredEvents.map((event) => (
                      <div key={event.id} className="flex items-start justify-between p-4 rounded-lg bg-slate-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{event.title}</h3>
                            <Badge className={CALENDAR_EVENT_TYPES[event.event_type]?.color || 'bg-slate-100'}>
                              {CALENDAR_EVENT_TYPES[event.event_type]?.label || event.event_type}
                            </Badge>
                          </div>
                          {event.description && <p className="text-sm text-slate-500 mt-1">{event.description}</p>}
                          {event.location && (
                            <p className="text-sm text-slate-400 mt-1 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />{event.location}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteEvent(event.id)}>
                          <Trash2 className="w-4 h-4 text-slate-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="shifts">
                {filteredShifts.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun shift planifié ce jour</p>
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => {
                          resetShiftForm();
                          setShiftForm(prev => ({ ...prev, start_date: format(selectedDate, 'yyyy-MM-dd') }));
                          setShiftDialogOpen(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />Créer un shift
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredShifts.map((shift) => (
                      <div 
                        key={shift.id} 
                        className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                        onClick={() => openShiftDetail(shift)}
                        data-testid={`shift-item-${shift.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900">{shift.name}</h3>
                            <Badge className={getEventTypeStyle(shift.event_type)}>{getEventTypeLabel(shift.event_type)}</Badge>
                            {shift.assign_all && <Badge variant="outline" className="text-xs">Tous</Badge>}
                          </div>
                          {shift.description && <p className="text-sm text-slate-500 mt-1 line-clamp-1">{shift.description}</p>}
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-3 h-3" />
                              {format(new Date(shift.start_date), 'dd/MM/yyyy')}
                              {shift.end_date && shift.end_date !== shift.start_date && ` - ${format(new Date(shift.end_date), 'dd/MM/yyyy')}`}
                            </span>
                            {!shift.assign_all && shift.assigned_to_names?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {shift.assigned_to_names.length} assigné(s)
                              </span>
                            )}
                            {shift.tasks?.length > 0 && (
                              <span>{shift.tasks.length} tâche(s)</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isAdmin && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); openEditShift(shift); }}
                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                data-testid={`edit-shift-${shift.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handleDeleteShift(shift.id); }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-shift-${shift.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Calendar Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvel événement calendrier</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateEvent} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date début *</Label>
                <Input type="datetime-local" value={eventForm.start_date} onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Date fin</Label>
                <Input type="datetime-local" value={eventForm.end_date} onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meeting">Réunion</SelectItem>
                    <SelectItem value="task">Tâche</SelectItem>
                    <SelectItem value="reminder">Rappel</SelectItem>
                    <SelectItem value="holiday">Congé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Lieu</Label>
                <Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Shift Dialog */}
      <Dialog open={shiftDialogOpen} onOpenChange={setShiftDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Créer un shift</DialogTitle></DialogHeader>
          {renderShiftForm(handleCreateShift, false)}
        </DialogContent>
      </Dialog>

      {/* Edit Shift Dialog */}
      <Dialog open={editShiftDialogOpen} onOpenChange={setEditShiftDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier le shift</DialogTitle></DialogHeader>
          {renderShiftForm(handleUpdateShift, true)}
        </DialogContent>
      </Dialog>

      {/* Shift Detail Dialog */}
      <Dialog open={shiftDetailOpen} onOpenChange={setShiftDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedShift?.name}
              <Badge className={getEventTypeStyle(selectedShift?.event_type)}>{getEventTypeLabel(selectedShift?.event_type)}</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedShift && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Date</p>
                  <p className="font-medium">
                    {format(new Date(selectedShift.start_date), 'dd MMMM yyyy', { locale: fr })}
                    {selectedShift.end_date && selectedShift.end_date !== selectedShift.start_date && 
                      ` - ${format(new Date(selectedShift.end_date), 'dd MMMM yyyy', { locale: fr })}`}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Créé par</p>
                  <p className="font-medium">{selectedShift.created_by_name}</p>
                </div>
              </div>
              
              {selectedShift.description && (
                <div>
                  <p className="text-slate-500 text-sm">Description</p>
                  <p className="mt-1">{selectedShift.description}</p>
                </div>
              )}
              
              <div>
                <p className="text-slate-500 text-sm mb-2">Assigné à</p>
                {selectedShift.assign_all ? (
                  <Badge variant="outline">Tous les utilisateurs</Badge>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selectedShift.assigned_to_names?.map((name, i) => (
                      <Badge key={i} variant="secondary">{name}</Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {selectedShift.tasks?.length > 0 && (
                <div>
                  <p className="text-slate-500 text-sm mb-2">Tâches</p>
                  <div className="space-y-1">
                    {selectedShift.tasks.map((task, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <span className="w-5 h-5 rounded-full bg-slate-200 text-xs flex items-center justify-center">{i + 1}</span>
                        <span className="text-sm">{task}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedShift.notes && (
                <div>
                  <p className="text-slate-500 text-sm">Notes</p>
                  <p className="mt-1 text-sm">{selectedShift.notes}</p>
                </div>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-xs text-slate-400">
                  Ce shift est visible dans Suivi du temps → Feuille des événements
                </p>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setShiftDetailOpen(false); openEditShift(selectedShift); }}>
                      <Edit className="w-4 h-4 mr-2" />Modifier
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteShift(selectedShift.id)}>
                      <Trash2 className="w-4 h-4 mr-2" />Supprimer
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
